import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
        this.#seekToVariantMedia(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Moves the gallery to the slide holding the selected variant's image.
   *
   * The template keeps media in admin order (keep_media_order), so the hero
   * stays on slide 1 and the per-colour shots sit at the end. Selecting a
   * colour therefore has to seek rather than rely on the variant image being
   * promoted to the front.
   *
   * Two things make a naive call unreliable, both seen on this theme:
   *   1. Called in the same frame the gallery is replaced, the slideshow's
   *      refs are not resolved yet and select() silently no-ops.
   *   2. The animated form of select() updates the index and aria-hidden but
   *      the track never scrolls, leaving the viewer on slide 1. The theme's
   *      own zoom handler avoids this with { animate: false }.
   * So: retry on a timer until the slideshow exists, select without
   * animation, then pin scrollLeft directly as a belt-and-braces fallback.
   *
   * @param {Element} gallery - The freshly inserted media gallery.
   */
  #seekToVariantMedia(gallery) {
    const variantMediaId = gallery.dataset.variantMediaId;
    if (!variantMediaId) return;

    const slides = [...gallery.querySelectorAll('slideshow-slide')];
    const targetIndex = slides.findIndex((slide) =>
      slide.querySelector(`.product-media[data-media-id="${variantMediaId}"]`)
    );
    if (targetIndex < 1) return;

    let attempts = 0;
    const apply = () => {
      const slideshow = gallery.slideshow;
      const track = gallery.querySelector('slideshow-slides');
      const target = slides[targetIndex];

      if ((!slideshow || !track) && attempts < 20) {
        attempts++;
        setTimeout(apply, 40);
        return;
      }

      // The track is scroll-behavior:smooth with scroll snapping, so moving it
      // animates across every slide in between. From slide 1 to slide 11 that
      // reads as the gallery flicking through the whole set. Force an instant
      // jump for the duration of the seek, then hand the property back.
      const previousBehavior = track ? track.style.scrollBehavior : null;
      if (track) track.style.scrollBehavior = 'auto';

      if (slideshow) {
        try {
          slideshow.select(targetIndex, undefined, { animate: false });
        } catch (error) {
          console.warn('[media-gallery] select() failed:', error);
        }
      }

      if (track && target && Math.abs(track.scrollLeft - target.offsetLeft) > 1) {
        track.scrollLeft = target.offsetLeft;
      }

      if (track) track.style.scrollBehavior = previousBehavior || '';
    };

    // Timers rather than requestAnimationFrame: rAF is throttled to zero in
    // background or non-painting contexts, which would leave the seek
    // permanently pending. Fire once immediately, then re-assert twice, since
    // the slideshow resets its index while it finishes wiring itself up.
    setTimeout(apply, 0);
    setTimeout(apply, 120);
    setTimeout(apply, 400);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
